import type { ProviderClaim } from '../../../api/server/models/ProviderClaim';
import type { ProviderPatientClaimStatement } from '../../../api/server/models/ProviderPatientClaimStatement';
import { isStatementClaimPendingAdjudication } from './statement-claim-financials.utils';
import { parseMoney } from './statement-format.utils';

/**
 * What the patient owes on each claim the payer has not adjudicated yet, keyed by claim id.
 *
 * The server reports $0 balance due while a claim is pending, so the printed statement showed
 * Amount Due $0.00 next to the full charges. Exer asked for those charges to count toward Amount
 * Due (FE-161): the billed charge less the patient's net payments and discounts, floored at $0.
 * Adjudicated claims are absent from the map — their server balance stands.
 */
export const resolvePendingAmountDueByClaimId = (
    claims: ProviderPatientClaimStatement[],
    providerClaimsById: Map<string, ProviderClaim>,
): Map<string, number> => {
    const result = new Map<string, number>();
    for (const claim of claims) {
        if (!claim.id) continue;
        if (!isStatementClaimPendingAdjudication(claim, providerClaimsById.get(claim.id))) continue;

        const charge = (claim.serviceLines || []).reduce(
            (sum, line) => sum + parseMoney(line.chargeAmount?.amount),
            0,
        );
        // Refunds are negative payments, so this is already net.
        const paid = (claim.patientPayments || []).reduce(
            (sum, payment) => sum + parseMoney(payment.amount?.amount),
            0,
        );
        // Discounts may be posted as negative invoice items; the write-off is the magnitude.
        const discounted = (claim.discountLines || []).reduce(
            (sum, line) => sum + Math.abs(parseMoney(line.amount?.amount)),
            0,
        );
        result.set(claim.id, Math.max(0, charge - paid - discounted));
    }
    return result;
};

/**
 * How much to add to the server's balance for the given claims: each pending claim's amount due
 * replaces whatever the server already counted for it (normally $0).
 */
export const sumPendingAmountDueAdjustment = (
    claims: ProviderPatientClaimStatement[],
    pendingAmountDueByClaimId: Map<string, number>,
    claimIds: Set<string>,
): number =>
    claims.reduce((total, claim) => {
        const pending = claim.id && claimIds.has(claim.id) ? pendingAmountDueByClaimId.get(claim.id) : undefined;
        return pending === undefined ? total : total + pending - parseMoney(claim.balanceDue?.amount);
    }, 0);
